import type { AxiosResponse } from 'axios';

import type { LogoutType } from 'shared/clients/client-spa-AccountGroup';
import type { SsoConfig } from 'shared/typings/appSettings';

import type { AuthModel } from '../models/authorizationState.model';
import { BEFORE_CONTEXT_CHANGE_ACTION } from '../models/beforeContextChangeAction';
import type { Profile } from '../services/Authorization.contracts';
import AuthorizationModuleService from '../services/authorizationModule.service';
import AuthorizationModuleStore from './authorizationModule.store';

class AuthorizationModuleFacade {
  get accessToken(): string | undefined {
    return AuthorizationModuleStore.UserAccessToken;
  }

  get isUserLogged(): boolean {
    return !!AuthorizationModuleStore.UserCurrentAuthorizationState?.isLogged;
  }

  get getAuthMethod() {
    return AuthorizationModuleStore.getAuthMethod;
  }

  getUserAuthDataFromSessionStorage() {
    return AuthorizationModuleStore.UserCurrentAuthorizationState;
  }

  clearAuthorizationState() {
    return AuthorizationModuleStore.clearAuthorizationState();
  }

  async beforeContextChange() {
    return Promise.resolve(BEFORE_CONTEXT_CHANGE_ACTION.CONTINUE);
  }

  setBeforeContextChange(beforeContextChange: () => Promise<BEFORE_CONTEXT_CHANGE_ACTION>) {
    this.beforeContextChange = beforeContextChange;
  }

  resetBeforeContextChange() {
    this.beforeContextChange = () => Promise.resolve(BEFORE_CONTEXT_CHANGE_ACTION.CONTINUE);
  }

  async getTokenForChatBot(
    ssoConfig: SsoConfig,
    errorCallback = (error: any) => error
  ): Promise<AxiosResponse | void> {
    try {
      return await AuthorizationModuleService.getTokenForChatBot(ssoConfig, this.accessToken);
    } catch (e) {
      errorCallback(e);
    }
  }

  async getTokenForMakeAnAppointment(
    ssoConfig: SsoConfig,
    errorCallback = (error: any) => error
  ): Promise<AxiosResponse | void> {
    try {
      return await AuthorizationModuleService.getTokenForMakeAnAppointment(ssoConfig, this.accessToken!);
    } catch (e) {
      errorCallback(e);
    }
  }

  async loginAsNaturalPerson(
    successCallback = () => {},
    errorCallback = (error: any) => error
  ): Promise<void> {
    const beforeContextChangeAction = await this.beforeContextChange();
    if(beforeContextChangeAction === BEFORE_CONTEXT_CHANGE_ACTION.STOP) {
      return;
    }
    try {
      AuthorizationModuleService.loginAsNaturalPerson();
      successCallback();
    } catch (e) {
      errorCallback(e);
    }
  }

  async loginAsOrganization(
    organizationNip: string,
    successCallback = () => {},
    errorCallback = (error: any) => error
  ): Promise<void> {
    const beforeContextChangeAction = await this.beforeContextChange();
    if (beforeContextChangeAction === BEFORE_CONTEXT_CHANGE_ACTION.STOP) {
      return;
    }

    try {
      AuthorizationModuleService.loginAsOrganization(organizationNip);
      successCallback();
    } catch (e) {
      errorCallback(e);
    }
  }

  async loginAsDelegationUser(
    delegationPrincipalPesel: string,
    successCallback = () => {},
    errorCallback = (error: any) => error
  ): Promise<void> {
    const beforeContextChangeAction = await this.beforeContextChange();
    if (beforeContextChangeAction === BEFORE_CONTEXT_CHANGE_ACTION.STOP) {
      return;
    }

    try {
      AuthorizationModuleService.loginAsDelegationUser(delegationPrincipalPesel);
      successCallback();
    } catch (e) {
      errorCallback(e);
    }
  }

  async loginAsDelegationOrganization(
    delegationPrincipalNip: string,
    successCallback = () => {},
    errorCallback = (error: any) => error
  ): Promise<void> {
    const beforeContextChangeAction = await this.beforeContextChange();
    if (beforeContextChangeAction === BEFORE_CONTEXT_CHANGE_ACTION.STOP) {
      return;
    }

    try {
      AuthorizationModuleService.loginAsDelegationOrganization(delegationPrincipalNip);
      successCallback();
    } catch (e) {
      errorCallback(e);
    }
  }

  logout(logoutType: LogoutType, callback = () => {}) {
    AuthorizationModuleStore.clearAuthorizationState();
    AuthorizationModuleService.clearOrgId();
    AuthorizationModuleService.clearDelegationPrincipalPesel();
    AuthorizationModuleService.clearDelegationPrincipalNip();
    callback();
    AuthorizationModuleService.postLogoutMessage(logoutType);
  }

  setUserAuth (authObj: AuthModel | any) {
    return AuthorizationModuleStore.setUserAuth(authObj);
  }

  public get UserCurrentAuthorizationState() {
    return AuthorizationModuleStore.UserCurrentAuthorizationState;
  }

  public get UserIdToken() {
    return AuthorizationModuleStore.UserIdToken;
  }

  public get UserProfile(): Profile | undefined {
    return AuthorizationModuleStore.UserProfile;
  }

  public get UserProfileAuthClaims(): Profile | undefined {
    return AuthorizationModuleStore.UserProfileAuthClaims;
  }

  public get RedirectName(): string | undefined {
    return AuthorizationModuleStore.State?.redirectName;
  }

  public get ExpiresAt() {
    return AuthorizationModuleStore.ExpiresAt;
  }

  public get AuthErrorState() {
    return AuthorizationModuleStore.authErrorState;
  }

  public get UserAccessToken() {
    return AuthorizationModuleStore.UserAccessToken;
  }

  public get UserAccessTokenComputedRef() {
    return AuthorizationModuleStore.UserAccessTokenComputedRef;
  }

  public get UserSid() {
    return AuthorizationModuleStore.userSid;
  }

  public get UserMid() {
    return AuthorizationModuleStore.userMid;
  }

  public get isOrganization() {
    return AuthorizationModuleStore.isOrganization;
  }

  public get isDelegation() {
    return AuthorizationModuleStore.isDelegation;
  }

  public get delegationId() {
    return AuthorizationModuleStore.delegationId;
  }
}

export default new AuthorizationModuleFacade;
